/**
 * Application Configuration
 * Single source of truth for all environment variables
 *
 * NOTE: NEXT_PUBLIC_* vars must be accessed directly (not dynamically)
 * because Next.js inlines them at build time
 */

if (typeof window === 'undefined') {
  const missing = [
    'NEXT_PUBLIC_KEYCLOAK_URL',
    'NEXT_PUBLIC_KEYCLOAK_REALM',
    'NEXT_PUBLIC_KEYCLOAK_CLIENT_ID',
    'NEXT_PUBLIC_BACKEND_URL',
    'NEXT_PUBLIC_APP_URL',
  ].filter(k => !process.env[k])

  if (missing.length) {
    const msg = `Missing required environment variables:\n${missing.join('\n')}`
    if (process.env.NODE_ENV === 'production') throw new Error(`[FATAL] ${msg}`)
    console.warn(`[WARNING] ${msg}`)
  }
}

/**
 * API Configuration
 */
export const apiConfig = {
  /** Backend API base URL */
  backendUrl: process.env.NEXT_PUBLIC_BACKEND_URL || '',
  /** API prefix (e.g., /api/v1) - defaults to /api/v1 as used by backend */
  apiPrefix: process.env.NEXT_PUBLIC_API_PREFIX || '/api/v1',
  /** Full API URL (backendUrl + apiPrefix) */
  get apiUrl() {
    return `${this.backendUrl}${this.apiPrefix}`
  },
  /** Request timeout in milliseconds */
  timeout: Number(process.env.NEXT_PUBLIC_API_TIMEOUT) || 30000,
} as const

/**
 * Keycloak Authentication Configuration
 *
 * Keycloak is only contacted server-side now (BFF pattern). The browser
 * talks to /api/auth/* on the Next.js origin; tokens never reach JS.
 * NEXT_PUBLIC_* vars are kept for the SSR helper to read the realm URL.
 */
export const keycloakConfig = {
  url: process.env.NEXT_PUBLIC_KEYCLOAK_URL || '',
  realm: process.env.NEXT_PUBLIC_KEYCLOAK_REALM || '',
  clientId: process.env.NEXT_PUBLIC_KEYCLOAK_CLIENT_ID || '',

  /** Get Keycloak realm URL */
  get realmUrl() {
    return `${this.url}/realms/${this.realm}`
  },
} as const

/**
 * Application Configuration
 */
export const appConfig = {
  /** Application URL */
  url: process.env.NEXT_PUBLIC_APP_URL || 'http://localhost:3000',
  /** Debug mode */
  debug: process.env.NEXT_PUBLIC_DEBUG === 'true',
  /** Enable mock data */
  enableMockData: process.env.NEXT_PUBLIC_ENABLE_MOCK_DATA === 'true',
} as const

/**
 * Combined config export for backward compatibility
 */
export const config = {
  api: apiConfig,
  keycloak: keycloakConfig,
  app: appConfig,
  // Shorthand accessors
  get apiUrl() {
    return apiConfig.apiUrl
  },
  get backendUrl() {
    return apiConfig.backendUrl
  },
} as const

export default config
